import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { X, Loader2, Mail, Lock, User as UserIcon, Phone, Calendar, Briefcase } from "lucide-react";

const PROFESSIONS = [
  "Speech-Language Pathologist",
  "AAC Specialist",
  "Special Educator",
  "Occupational Therapist",
  "Parent / Caregiver",
  "Student Clinician",
  "Other",
];

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+64", label: "🇳🇿 +64" },
];

function readApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Field = ({ icon: Icon, children }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />}
    <div className={Icon ? "[&>input]:pl-9 [&>select]:pl-9" : ""}>{children}</div>
  </div>
);

export default function AuthModal() {
  const { authModal, closeAuth, setAuth, user } = useAuth();
  const [mode, setMode] = useState(authModal.mode);
  const [busy, setBusy] = useState(false);

  // Sign-in state
  const [signinEmail, setSigninEmail] = useState("");
  const [signinPwd, setSigninPwd] = useState("");
  // Forgot password inline state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // Sign-up state
  const [name, setName] = useState("");
  const [profession, setProfession] = useState("");
  const [professionOther, setProfessionOther] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");

  React.useEffect(() => {
    if (authModal.open) setMode(authModal.mode);
  }, [authModal.open, authModal.mode]);

  if (!authModal.open) return null;

  const handleSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: signinEmail.trim(), password: signinPwd });
      setAuth(res.data.access_token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}`);
      closeAuth();
    } catch (err) {
      toast.error(readApiError(err?.response?.data?.detail) || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      // Endpoint always 200s — but just in case
      toast.error(readApiError(err?.response?.data?.detail) || "Could not request reset");
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (pwd !== confirmPwd) { toast.error("Passwords do not match"); return; }
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!profession) { toast.error("Please choose a profession"); return; }
    if (profession === "Other" && !professionOther.trim()) { toast.error("Please specify your profession"); return; }
    if (!dob) { toast.error("Date of birth is required"); return; }
    // Client-side 18+ check
    const dobDate = new Date(dob);
    const eighteenYrsAgo = new Date(); eighteenYrsAgo.setFullYear(eighteenYrsAgo.getFullYear() - 18);
    if (dobDate > eighteenYrsAgo) { toast.error("You must be 18 or older to sign up"); return; }

    setBusy(true);
    const body = {
      name: name.trim(),
      profession,
      profession_other: profession === "Other" ? professionOther.trim() : null,
      date_of_birth: dob,
      email: email.trim(),
      password: pwd,
      confirm_password: confirmPwd,
      contact_country_code: countryCode,
      contact_phone: phone.replace(/\D/g, ""),
    };
    try {
      // If currently a guest, UPGRADE in place to preserve history; else REGISTER new
      const endpoint = user?.is_guest ? `${API}/auth/upgrade-guest` : `${API}/auth/register`;
      const headers = user?.is_guest ? { Authorization: `Bearer ${localStorage.getItem("sensiiq_token")}` } : {};
      const res = await axios.post(endpoint, body, { headers });
      setAuth(res.data.access_token, res.data.user);
      toast.success(`Welcome to CommPass, ${res.data.user.name.split(" ")[0]}!`);
      closeAuth();
    } catch (err) {
      toast.error(readApiError(err?.response?.data?.detail) || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 backdrop-blur-sm px-4 py-6" data-testid="auth-modal">
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl border border-[#E2E8F0]">
        <button
          onClick={closeAuth}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#F4F5F0] hover:bg-[#E6F0E9] flex items-center justify-center text-[#52606D]"
          data-testid="auth-modal-close">
          <X className="w-4 h-4" />
        </button>

        <div className="px-7 pt-8 pb-2">
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">
            {mode === "signin" ? "Welcome back" : "Create your CommPass account"}
          </span>
          <h2 className="font-display text-2xl text-[#2C4C3B] mt-1">
            {mode === "signin" ? "Sign in to continue" : "Sign up to keep building"}
          </h2>
          {authModal.reason && (
            <p className="mt-2 text-sm text-[#52606D]" data-testid="auth-modal-reason">{authModal.reason}</p>
          )}
        </div>

        {/* Tabs */}
        <div className="px-7 mt-4 grid grid-cols-2 gap-2 bg-[#F4F5F0] rounded-full p-1 mx-7 my-2" style={{ width: "calc(100% - 56px)" }}>
          <button
            onClick={() => setMode("signin")}
            className={`text-sm py-2 rounded-full font-medium transition-colors ${mode === "signin" ? "bg-white text-[#2C4C3B] shadow-sm" : "text-[#52606D]"}`}
            data-testid="auth-tab-signin">
            Sign in
          </button>
          <button
            onClick={() => setMode("signup")}
            className={`text-sm py-2 rounded-full font-medium transition-colors ${mode === "signup" ? "bg-white text-[#2C4C3B] shadow-sm" : "text-[#52606D]"}`}
            data-testid="auth-tab-signup">
            Sign up
          </button>
        </div>

        {mode === "signin" ? (
          <form className="px-7 pb-7 pt-4 space-y-3" onSubmit={handleSignin} data-testid="signin-form">
            <Field icon={Mail}>
              <Input type="email" required value={signinEmail} onChange={(e) => setSigninEmail(e.target.value)}
                placeholder="Email" className="rounded-xl" data-testid="signin-email" autoComplete="email" />
            </Field>
            <Field icon={Lock}>
              <Input type="password" required value={signinPwd} onChange={(e) => setSigninPwd(e.target.value)}
                placeholder="Password" className="rounded-xl" data-testid="signin-password" autoComplete="current-password" />
            </Field>
            <div className="text-right">
              <button type="button" onClick={() => { setForgotOpen(true); setForgotEmail(signinEmail); }}
                className="text-xs text-[#52606D] hover:text-[#2C4C3B]" data-testid="auth-forgot-link">
                Forgot password?
              </button>
            </div>
            <Button type="submit" disabled={busy}
              className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#1F3A2C] text-white py-6" data-testid="signin-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in…</> : "Sign in"}
            </Button>

            {forgotOpen && (
              <div className="mt-6 pt-5 border-t border-[#E2E8F0]" data-testid="forgot-password-panel">
                {forgotSent ? (
                  <div className="p-4 rounded-xl bg-[#E6F0E9] border border-[#2C4C3B]/15 text-sm text-[#2C4C3B]" data-testid="forgot-password-sent">
                    <strong>Check your inbox.</strong> If an account exists for that email, we've sent a reset link. The link is valid for 60 minutes.
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="forgot-password-form-wrap">
                    <p className="text-xs text-[#52606D]">Enter your account email and we'll send you a reset link.</p>
                    <Field icon={Mail}>
                      <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="Your email" className="rounded-xl" data-testid="forgot-password-email" />
                    </Field>
                    <div className="flex items-center gap-2">
                      <Button type="button" onClick={handleForgot} disabled={busy || !forgotEmail.trim()}
                        className="rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white px-5" data-testid="forgot-password-send">
                        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send reset link"}
                      </Button>
                      <button type="button" onClick={() => { setForgotOpen(false); setForgotSent(false); }}
                        className="text-xs text-[#52606D] hover:text-[#2C4C3B]" data-testid="forgot-password-cancel">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
        ) : (
          <form className="px-7 pb-7 pt-4 space-y-3" onSubmit={handleSignup} data-testid="signup-form">
            <Field icon={UserIcon}>
              <Input required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Full name" className="rounded-xl" data-testid="signup-name" autoComplete="name" />
            </Field>

            <Field icon={Briefcase}>
              <select required value={profession} onChange={(e) => setProfession(e.target.value)}
                className="w-full rounded-xl border border-[#E2E8F0] bg-white py-2 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 h-10 pl-9 pr-3"
                data-testid="signup-profession">
                <option value="">Profession</option>
                {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            {profession === "Other" && (
              <Input required value={professionOther} onChange={(e) => setProfessionOther(e.target.value)}
                placeholder="Please specify your profession" className="rounded-xl" data-testid="signup-profession-other" />
            )}

            <Field icon={Calendar}>
              <Input type="date" required value={dob} onChange={(e) => setDob(e.target.value)}
                className="rounded-xl" data-testid="signup-dob" max={new Date().toISOString().slice(0, 10)} />
            </Field>

            <Field icon={Mail}>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email" className="rounded-xl" data-testid="signup-email" autoComplete="email" />
            </Field>

            <div className="grid grid-cols-[110px_1fr] gap-2">
              <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                className="w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 h-10"
                data-testid="signup-country-code">
                {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              <Field icon={Phone}>
                <Input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="Contact number" className="rounded-xl" data-testid="signup-phone" autoComplete="tel" />
              </Field>
            </div>

            <Field icon={Lock}>
              <Input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder="New password (min 8 characters)" className="rounded-xl" data-testid="signup-password" autoComplete="new-password" />
            </Field>
            <Field icon={Lock}>
              <Input type="password" required minLength={8} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Confirm password" className="rounded-xl" data-testid="signup-confirm-password" autoComplete="new-password" />
            </Field>

            <p className="text-xs text-[#52606D] leading-relaxed">
              By creating an account you confirm you are <strong>18 or older</strong> and agree to use CommPass as a clinical reference tool, not a substitute for individualised assessment.
            </p>

            <Button type="submit" disabled={busy}
              className="w-full rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white py-6" data-testid="signup-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating your account…</> : "Create account"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
